import { useEffect, useMemo, useRef, useState } from "react";
import {
    GetRoleList,
    GetAllPermissions,
    GetRolePermissions,
    AddRole,
    RemoveRole,
    UpdateRolePermissions,
    GetUserRoles,
    AssignRoleToUser,
    RemoveRoleFromUser,
    GetRoleScopes,
    UpdateRoleScope,
} from "../services/RoleService";
import { GetAllUser } from "../services/UserService"; // مسیر/اسم واقعی خودت
import { useLanguage } from "./LanguageContext"; // مسیر واقعی خودت

/* -------------------------------------------------------------------------- */
/*  فونت پایه                                                                  */
/* -------------------------------------------------------------------------- */
const FONT = "IRANSansWeb, Vazirmatn, sans-serif";
const SUPER_ADMIN_NAME = "SuperAdmin";
const PRIMARY_ADMIN_USERNAME = "admin";

/* -------------------------------------------------------------------------- */
/*  متن‌ها                                                                     */
/* -------------------------------------------------------------------------- */
const TEXTS = {
    fa: {
        dir: "rtl",
        title: "مدیریت نقش‌ها و دسترسی‌ها",
        subtitle: "برای هر نقش دسترسی تعیین کنید و نقش‌ها را به کاربران اختصاص دهید",
        tabRoles: "نقش‌ها و دسترسی‌ها",
        tabAssign: "تخصیص نقش به کاربران",

        // ستون نقش‌ها
        rolesListTitle: "نقش‌ها",
        newRolePlaceholder: "نام نقش جدید...",
        addRole: "افزودن",
        deleteRoleHint: "حذف نقش",
        superAdminBadge: "پیش‌فرض",
        roleNameRequired: "نام نقش را وارد کنید",

        // پنل دسترسی‌ها
        selectRoleHint: "یک نقش را از سمت راست انتخاب کنید تا دسترسی‌هایش را ببینید",
        superAdminNote: "دسترسی‌های ابرکاربر به‌صورت خودکار کامل است و قابل تغییر نیست",
        selectAll: "انتخاب همه‌ی دسترسی‌ها",
        selectedCount: (n, total) => `${n} از ${total} دسترسی انتخاب شده`,
        save: "ذخیره دسترسی‌ها",
        saving: "در حال ذخیره...",

        // مودال حذف
        confirmDeleteTitle: "حذف نقش",
        confirmDeleteMsg: (name) => `آیا از حذف نقش «${name}» مطمئن هستید؟`,
        yesDelete: "بله، حذف کن",
        cancelDelete: "انصراف",
        deleting: "در حال حذف...",

        // تب تخصیص نقش
        searchUserPlaceholder: "جستجوی کاربر...",
        selectUserHint: "یک کاربر را از سمت راست انتخاب کنید",
        currentRolesTitle: "نقش‌های فعلی این کاربر",
        noRolesAssigned: "هنوز هیچ نقشی به این کاربر اختصاص داده نشده",
        selectRoleToAssign: "یک نقش برای افزودن انتخاب کنید",
        assignRole: "اختصاص نقش",
        assigning: "در حال اختصاص...",
        removeRoleFromUser: "حذف این نقش",
        noMoreRolesToAssign: "همه‌ی نقش‌ها به این کاربر اختصاص داده شده",

        // Toast ها
        toastFetchFail: "خطا در دریافت اطلاعات",
        toastAddRoleSuccess: "نقش با موفقیت ایجاد شد",
        toastAddRoleFail: "خطا در ایجاد نقش",
        toastDeleteRoleSuccess: "نقش با موفقیت حذف شد",
        toastDeleteRoleFail: "خطا در حذف نقش",
        toastSaveSuccess: "دسترسی‌های نقش با موفقیت ذخیره شد",
        toastSaveFail: "خطا در ذخیره‌ی دسترسی‌ها",
        toastAssignSuccess: "نقش با موفقیت به کاربر اختصاص یافت",
        toastAssignFail: "خطا در اختصاص نقش",
        toastRemoveSuccess: "نقش با موفقیت از کاربر حذف شد",
        toastRemoveFail: "خطا در حذف نقش از کاربر",

        loading: "در حال بارگذاری...",
        noSearchResult: "نتیجه‌ای یافت نشد",

        permissionModules: {
            City: "شهر", Province: "استان", Customer: "مشتری", Employee: "کارمند",
            Grouping: "گروه‌بندی", Product: "کالا", ProductPrice: "قیمت کالا",
            Post: "سمت", Order: "سفارش", OrderItem: "آیتم سفارش", User: "کاربر سیستم",
        },
        permissionActions: {
            View: "مشاهده", Create: "افزودن", Edit: "ویرایش", Delete: "حذف",
            ChangeStatus: "تغییر وضعیت", RecalculatePrice: "محاسبه‌ی مجدد قیمت",
        },

        scopeLabel: "دامنه دسترسی",
        scopeLabels: {
            Self: "فقط خودم",
            Team: "خودم و زیرمجموعه‌ها",
            All: "همه",
        },
        toastScopeSaveSuccess: "دامنه دسترسی با موفقیت ذخیره شد",
        toastScopeSaveFail: "خطا در ذخیره‌ی دامنه دسترسی",
    },
    ar: {
        dir: "rtl",
        title: "إدارة الأدوار والصلاحيات",
        subtitle: "حدّد صلاحيات كل دور وقم بتخصيص الأدوار للمستخدمين",
        tabRoles: "الأدوار والصلاحيات",
        tabAssign: "تخصيص الأدوار للمستخدمين",

        rolesListTitle: "الأدوار",
        newRolePlaceholder: "اسم الدور الجديد...",
        addRole: "إضافة",
        deleteRoleHint: "حذف الدور",
        superAdminBadge: "افتراضي",
        roleNameRequired: "أدخل اسم الدور",

        selectRoleHint: "اختر دورًا من اليمين لعرض صلاحياته",
        superAdminNote: "صلاحيات المشرف العام كاملة تلقائيًا ولا يمكن تعديلها",
        selectAll: "تحديد جميع الصلاحيات",
        selectedCount: (n, total) => `${n} من ${total} صلاحية محددة`,
        save: "حفظ الصلاحيات",
        saving: "جارٍ الحفظ...",

        confirmDeleteTitle: "حذف الدور",
        confirmDeleteMsg: (name) => `هل أنت متأكد من حذف الدور «${name}»؟`,
        yesDelete: "نعم، احذف",
        cancelDelete: "إلغاء",
        deleting: "جارٍ الحذف...",

        searchUserPlaceholder: "بحث عن مستخدم...",
        selectUserHint: "اختر مستخدمًا من اليمين",
        currentRolesTitle: "الأدوار الحالية لهذا المستخدم",
        noRolesAssigned: "لم يتم تخصيص أي دور لهذا المستخدم بعد",
        selectRoleToAssign: "اختر دورًا لإضافته",
        assignRole: "تخصيص الدور",
        assigning: "جارٍ التخصيص...",
        removeRoleFromUser: "إزالة هذا الدور",
        noMoreRolesToAssign: "تم تخصيص جميع الأدوار لهذا المستخدم",

        toastFetchFail: "خطأ في جلب البيانات",
        toastAddRoleSuccess: "تم إنشاء الدور بنجاح",
        toastAddRoleFail: "خطأ في إنشاء الدور",
        toastDeleteRoleSuccess: "تم حذف الدور بنجاح",
        toastDeleteRoleFail: "خطأ في حذف الدور",
        toastSaveSuccess: "تم حفظ صلاحيات الدور بنجاح",
        toastSaveFail: "خطأ في حفظ الصلاحيات",
        toastAssignSuccess: "تم تخصيص الدور للمستخدم بنجاح",
        toastAssignFail: "خطأ في تخصيص الدور",
        toastRemoveSuccess: "تمت إزالة الدور من المستخدم بنجاح",
        toastRemoveFail: "خطأ في إزالة الدور",

        loading: "جارٍ التحميل...",
        noSearchResult: "لا توجد نتائج",

        permissionModules: {
            City: "المدينة", Province: "المحافظة", Customer: "العميل", Employee: "الموظف",
            Grouping: "التجميع", Product: "المنتج", ProductPrice: "سعر المنتج",
            Post: "المنصب", Order: "الطلب", OrderItem: "عنصر الطلب", User: "مستخدم النظام",
        },
        permissionActions: {
            View: "عرض", Create: "إضافة", Edit: "تعديل", Delete: "حذف",
            ChangeStatus: "تغيير الحالة", RecalculatePrice: "إعادة حساب السعر",
        },

        scopeLabel: "نطاق الوصول",
        scopeLabels: {
            Self: "أنا فقط",
            Team: "أنا وفريقي",
            All: "الجميع",
        },
        toastScopeSaveSuccess: "تم حفظ نطاق الوصول بنجاح",
        toastScopeSaveFail: "خطأ في حفظ نطاق الوصول",
    },
};

/* ترتیب و ظاهر هر ماژول در درخت دسترسی‌ها (مطابق Permissions.cs سمت بک‌اند) */
const MODULE_META = {
    City: { icon: "folder", color: "#6C3DFF" },
    Province: { icon: "folder", color: "#6C3DFF" },
    Customer: { icon: "users", color: "#10B981" },
    Employee: { icon: "users", color: "#F59E0B" },
    Grouping: { icon: "folder", color: "#6C3DFF" },
    Product: { icon: "box", color: "#10B981" },
    ProductPrice: { icon: "tag", color: "#F59E0B" },
    Post: { icon: "store", color: "#0EA5A5" },
    Order: { icon: "box", color: "#6C3DFF" },
    OrderItem: { icon: "box", color: "#6C3DFF" },
    User: { icon: "lock", color: "#EF4444" },
};
const MODULE_ORDER = ["City", "Province", "Customer", "Employee", "Grouping", "Product", "ProductPrice", "Post", "Order", "OrderItem", "User"];

/* ماژول‌هایی که منطق "خودم / زیرمجموعه‌ها / همه" براشون معنی داره.
   این لیست فقط سمت فرانت هاردکده و به هیچ endpoint جدیدی وابسته نیست. */
const SCOPABLE_MODULES = ["Employee", "Order", "OrderItem", "Customer"];
const SCOPE_VALUES = ["Self", "Team", "All"];

/* -------------------------------------------------------------------------- */
/*  آیکن‌ها                                                                    */
/* -------------------------------------------------------------------------- */
function Icon({ name, size = 14, color = "currentColor" }) {
    const paths = {
        plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
        trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></>,
        "chevron-down": <polyline points="6 9 12 15 18 9" />,
        search: <><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
        close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
        users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
        store: <><path d="M3 9l1-5h16l1 5" /><path d="M4 9v10a1 1 0 0 0 1 1h4v-6h6v6h4a1 1 0 0 0 1-1V9" /><path d="M3 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" /></>,
        box: <><path d="M21 8l-9-5-9 5 9 5 9-5z" /><path d="M3 8v8l9 5 9-5V8" /><path d="M12 13v8" /></>,
        folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />,
        check: <polyline points="20 6 9 17 4 12" />,
        alert: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
        tag: <><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3.24L4 3a1 1 0 0 0-1 1l.24 5.59a2 2 0 0 0 .59 1.41l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83Z" /><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" /></>,
        lock: <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
        shield: <path d="M12 2 4 5v6c0 5.2 3.6 9.1 8 11 4.4-1.9 8-5.8 8-11V5z" />,
        "user-plus": <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="17" y1="11" x2="23" y2="11" /></>,
    };
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
            fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            {paths[name] ?? null}
        </svg>
    );
}

function Toast({ message, visible, type }) {
    if (!visible) return null;
    return (
        <div style={{
            position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
            background: type === "error" ? "#EF4444" : "#10B981",
            color: "#fff", padding: "10px 20px", borderRadius: 12,
            fontSize: 12, fontWeight: 500, zIndex: 2000,
            display: "flex", alignItems: "center", gap: 8,
            fontFamily: FONT,
            boxShadow: type === "error" ? "0 8px 24px rgba(239,68,68,.3)" : "0 8px 24px rgba(16,185,129,.3)",
        }}>
            <Icon name={type === "error" ? "alert" : "check"} size={13} color="#fff" />
            {message}
        </div>
    );
}

const fieldBaseStyle = {
    width: "100%", height: 40, border: "1.5px solid #E8ECF4",
    borderRadius: 10, padding: "0 12px", fontSize: 12,
    fontFamily: FONT, direction: "rtl",
    outline: "none", color: "#1E293B", boxSizing: "border-box",
    background: "#F8FAFF", transition: "all .15s",
};

function TextField({ value, onChange, placeholder, onKeyDown }) {
    return (
        <input
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            style={fieldBaseStyle}
            onFocus={(e) => { e.target.style.borderColor = "#6C3DFF"; e.target.style.background = "#fff"; }}
            onBlur={(e) => { e.target.style.borderColor = "#E8ECF4"; e.target.style.background = "#F8FAFF"; }}
        />
    );
}

/* چک‌باکس سه‌حالته (خالی / نیمه‌انتخاب / کامل) برای نمایش وضعیت هر ماژول */
function TriCheckbox({ checked, indeterminate, onChange, disabled, size = 16 }) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return (
        <input
            ref={ref}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            style={{ width: size, height: size, cursor: disabled ? "not-allowed" : "pointer", accentColor: "#6C3DFF" }}
        />
    );
}

/* -------------------------------------------------------------------------- */
/*  کمک‌تابع‌ها                                                                 */
/* -------------------------------------------------------------------------- */
const normalizeRole = (raw) => ({
    id: raw.id ?? raw.Id,
    name: raw.name ?? raw.Name ?? "",
});

const normalizeUser = (raw) => ({
    id: raw.id ?? raw.Id,
    userName: raw.userName ?? raw.UserName ?? `#${raw.id ?? raw.Id}`,
});

function groupPermissions(allPermissions) {
    const groups = {};
    allPermissions.forEach((key) => {
        const [module] = key.split(".");
        if (!groups[module]) groups[module] = [];
        groups[module].push(key);
    });
    // اول ماژول‌های شناخته‌شده به ترتیب دلخواه، بعد هر ماژول ناشناخته‌ی احتمالی
    const known = MODULE_ORDER.filter((m) => groups[m]);
    const unknown = Object.keys(groups).filter((m) => !MODULE_ORDER.includes(m));
    return [...known, ...unknown].map((module) => ({ module, permissions: groups[module] }));
}

/* -------------------------------------------------------------------------- */
/*  تب‌های اصلی صفحه                                                           */
/* -------------------------------------------------------------------------- */
function MainTabs({ active, onChange, t }) {
    const tabs = [
        { key: "roles", label: t.tabRoles, icon: "shield" },
        { key: "assign", label: t.tabAssign, icon: "user-plus" },
    ];
    return (
        <div style={{ display: "flex", gap: 4, padding: "10px 14px", borderBottom: "1px solid #F1F5F9" }}>
            {tabs.map((tab) => {
                const isActive = active === tab.key;
                return (
                    <button
                        key={tab.key}
                        onClick={() => onChange(tab.key)}
                        style={{
                            flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                            padding: "9px 12px", borderRadius: 9, border: "none", cursor: "pointer",
                            fontFamily: FONT, fontSize: 11.5, fontWeight: 600, transition: "all .18s",
                            background: isActive ? "#EDE9FF" : "transparent",
                            color: isActive ? "#6C3DFF" : "#64748B",
                        }}
                    >
                        <Icon name={tab.icon} size={14} color={isActive ? "#6C3DFF" : "#94A3B8"} />
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  کامپوننت اصلی                                                              */
/* -------------------------------------------------------------------------- */
export default function RoleManagement() {
    const { lang } = useLanguage();
    const t = TEXTS[lang] || TEXTS.fa;

    const [activeTab, setActiveTab] = useState("roles");
    const [toast, setToast] = useState({ message: "", visible: false, type: "success" });
    const showToast = (message, type = "success") => {
        setToast({ message, visible: true, type });
        setTimeout(() => setToast((s) => ({ ...s, visible: false })), 2600);
    };

    /* ------------------------------ نقش‌ها و دسترسی‌ها ------------------------------ */
    const [roles, setRoles] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [checkedPermissions, setCheckedPermissions] = useState([]);
    const [loadingRolePerms, setLoadingRolePerms] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newRoleName, setNewRoleName] = useState("");
    const [addingRole, setAddingRole] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [expandedModule, setExpandedModule] = useState(null);
    const expandedModuleRef = useRef(null);

    /* دامنه دسترسی (Self/Team/All) هر ماژول برای نقش انتخاب‌شده */
    const [roleScopes, setRoleScopes] = useState({});
    const [loadingScopes, setLoadingScopes] = useState(false);
    const [savingScopeModule, setSavingScopeModule] = useState(null);

    const fetchRoles = async () => {
        try {
            const res = await GetRoleList();
            const raw = res.data?.data ?? res.data ?? [];
            setRoles(Array.isArray(raw) ? raw.map(normalizeRole) : []);
        } catch {
            showToast(t.toastFetchFail, "error");
        }
    };

    const fetchAllPermissions = async () => {
        try {
            const res = await GetAllPermissions();
            const raw = res.data?.data ?? res.data ?? [];
            setAllPermissions(Array.isArray(raw) ? raw : []);
        } catch {
            showToast(t.toastFetchFail, "error");
        }
    };

    useEffect(() => {
        (async () => {
            setLoadingRoles(true);
            await Promise.all([fetchRoles(), fetchAllPermissions()]);
            setLoadingRoles(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedRole = roles.find((r) => r.id === selectedRoleId) || null;
    const isSuperAdminSelected = selectedRole?.name === SUPER_ADMIN_NAME;

    const selectRole = async (role) => {
        setSelectedRoleId(role.id);
        if (role.name === SUPER_ADMIN_NAME) {
            setCheckedPermissions(allPermissions); // فقط نمایشی؛ غیرقابل ذخیره
            setRoleScopes({}); // سوپرادمین همیشه دسترسی کامل داره، نیازی به Scope نیست
            return;
        }
        setLoadingRolePerms(true);
        setLoadingScopes(true);
        try {
            const [permsRes, scopesRes] = await Promise.all([
                GetRolePermissions(role.id),
                GetRoleScopes(role.id),
            ]);

            const rawPerms = permsRes.data?.data ?? permsRes.data ?? [];
            setCheckedPermissions(Array.isArray(rawPerms) ? rawPerms : []);

            const rawScopes = scopesRes.data?.data ?? scopesRes.data ?? {};
            setRoleScopes(rawScopes && typeof rawScopes === "object" ? rawScopes : {});
        } catch {
            showToast(t.toastFetchFail, "error");
            setCheckedPermissions([]);
            setRoleScopes({});
        } finally {
            setLoadingRolePerms(false);
            setLoadingScopes(false);
        }
    };

    /* تغییر دامنه دسترسی یک ماژول: بلافاصله ذخیره می‌شه (مثل یه تنظیمات ساده)، با rollback در صورت خطا */
    const handleScopeChange = async (module, value) => {
        if (!selectedRoleId || isSuperAdminSelected) return;

        const previousValue = roleScopes[module] ?? "Self";
        if (previousValue === value) return;

        setRoleScopes((prev) => ({ ...prev, [module]: value }));
        setSavingScopeModule(module);
        try {
            await UpdateRoleScope(selectedRoleId, module, value);
            showToast(t.toastScopeSaveSuccess);
        } catch (err) {
            setRoleScopes((prev) => ({ ...prev, [module]: previousValue })); // بازگردانی در صورت خطا
            showToast(err?.response?.data ?? t.toastScopeSaveFail, "error");
        } finally {
            setSavingScopeModule(null);
        }
    };

    const grouped = useMemo(() => groupPermissions(allPermissions), [allPermissions]);

    const togglePermission = (key) => {
        if (isSuperAdminSelected) return;
        setCheckedPermissions((prev) =>
            prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
        );
    };

    const toggleModule = (permsInModule) => {
        if (isSuperAdminSelected) return;
        const allChecked = permsInModule.every((p) => checkedPermissions.includes(p));
        setCheckedPermissions((prev) => {
            if (allChecked) return prev.filter((p) => !permsInModule.includes(p));
            const merged = new Set([...prev, ...permsInModule]);
            return Array.from(merged);
        });
    };

    const toggleSelectAll = () => {
        if (isSuperAdminSelected) return;
        const allChecked = allPermissions.every((p) => checkedPermissions.includes(p));
        setCheckedPermissions(allChecked ? [] : [...allPermissions]);
    };

    const toggleExpandModule = (module) => {
        setExpandedModule((prev) => (prev === module ? null : module));
    };

    // وقتی ماژول جدیدی باز می‌شه، خودش رو داخل لیست اسکرول‌شونده به دید بیار
    useEffect(() => {
        if (expandedModule && expandedModuleRef.current) {
            expandedModuleRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    }, [expandedModule]);

    const handleSavePermissions = async () => {
        if (!selectedRoleId || isSuperAdminSelected) return;
        setSaving(true);
        try {
            await UpdateRolePermissions(selectedRoleId, checkedPermissions);
            showToast(t.toastSaveSuccess);
        } catch {
            showToast(t.toastSaveFail, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleAddRole = async () => {
        const name = newRoleName.trim();
        if (!name) { showToast(t.roleNameRequired, "error"); return; }
        setAddingRole(true);
        try {
            await AddRole(name);
            showToast(t.toastAddRoleSuccess);
            setNewRoleName("");
            await fetchRoles();
        } catch (err) {
            showToast(err?.response?.data ?? t.toastAddRoleFail, "error");
        } finally {
            setAddingRole(false);
        }
    };

    const handleDeleteRole = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await RemoveRole(deleteTarget.id);
            showToast(t.toastDeleteRoleSuccess);
            if (selectedRoleId === deleteTarget.id) { setSelectedRoleId(null); setCheckedPermissions([]); }
            setDeleteTarget(null);
            await fetchRoles();
        } catch (err) {
            showToast(err?.response?.data ?? t.toastDeleteRoleFail, "error");
        } finally {
            setDeleting(false);
        }
    };

    /* ------------------------------ تخصیص نقش به کاربر ------------------------------ */
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [userSearch, setUserSearch] = useState("");
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [userRoles, setUserRoles] = useState([]);
    const [loadingUserRoles, setLoadingUserRoles] = useState(false);
    const [roleToAssign, setRoleToAssign] = useState("");
    const [assigning, setAssigning] = useState(false);
    const [removingRoleName, setRemovingRoleName] = useState(null);

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            const res = await GetAllUser();
            const raw = res.data?.data ?? res.data ?? [];
            setUsers(Array.isArray(raw) ? raw.map(normalizeUser) : []);
        } catch {
            showToast(t.toastFetchFail, "error");
        } finally {
            setLoadingUsers(false);
        }
    };

    useEffect(() => {
        if (activeTab === "assign" && users.length === 0) fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const isPrimaryAdmin =
        (localStorage.getItem("userName") || "").toLowerCase() === PRIMARY_ADMIN_USERNAME;

    const selectedUser = users.find((u) => u.id === selectedUserId) || null;

    const selectUser = async (user) => {
        setSelectedUserId(user.id);
        setRoleToAssign("");
        setLoadingUserRoles(true);
        try {
            const res = await GetUserRoles(user.id);
            const raw = res.data?.data ?? res.data ?? [];
            setUserRoles(Array.isArray(raw) ? raw : []);
        } catch {
            showToast(t.toastFetchFail, "error");
            setUserRoles([]);
        } finally {
            setLoadingUserRoles(false);
        }
    };

    const visibleUsers = useMemo(() => {
        const term = userSearch.trim().toLowerCase();
        if (!term) return users;
        return users.filter((u) => u.userName.toLowerCase().includes(term));
    }, [users, userSearch]);

    const assignableRoles = useMemo(
        () => roles.filter((r) => !userRoles.includes(r.name)),
        [roles, userRoles]
    );

    const handleAssignRole = async () => {
        if (!selectedUserId || !roleToAssign) return;
        setAssigning(true);
        try {
            await AssignRoleToUser(selectedUserId, roleToAssign);
            showToast(t.toastAssignSuccess);
            setUserRoles((prev) => [...prev, roleToAssign]);
            setRoleToAssign("");
        } catch (err) {
            showToast(err?.response?.data ?? t.toastAssignFail, "error");
        } finally {
            setAssigning(false);
        }
    };

    const handleRemoveRoleFromUser = async (roleName) => {
        if (!selectedUserId) return;
        setRemovingRoleName(roleName);
        try {
            await RemoveRoleFromUser(selectedUserId, roleName);
            showToast(t.toastRemoveSuccess);
            setUserRoles((prev) => prev.filter((r) => r !== roleName));
        } catch (err) {
            showToast(err?.response?.data ?? t.toastRemoveFail, "error");
        } finally {
            setRemovingRoleName(null);
        }
    };

    /* ------------------------------ رندر ------------------------------ */
    const totalSelected = checkedPermissions.length;

    return (
        <div style={{ fontFamily: FONT, direction: t.dir, minHeight: "100vh", background: "#F5F6FA" }}>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
                .role-row, .user-row { transition: background .15s, border-color .15s; cursor: pointer; }
                .role-row:hover, .user-row:hover { background: #F8FAFF; }
                .icon-btn:hover:not(:disabled) { background: #F1F5F9 !important; }
                .primary-btn:hover:not(:disabled) { transform: translateY(-1px); }
                .module-row:hover { background: #FAFBFF; }
                .chip-x:hover { background: rgba(239,68,68,.12) !important; }
                @media (max-width: 760px) {
                    .rm-split { flex-direction: column !important; }
                    .rm-side { width: 100% !important; }
                }
            `}</style>

            {/* هدر */}
            <div style={{
                background: "linear-gradient(135deg, #6C3DFF 0%, #9B59FF 100%)",
                padding: "26px 28px 84px", position: "relative", overflow: "hidden",
            }}>
                <div style={{ position: "absolute", top: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
                <div style={{ position: "absolute", bottom: -40, right: 80, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <h1 style={{ fontSize: 19, fontWeight: 700, color: "#fff", margin: 0 }}>{t.title}</h1>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", margin: "4px 0 0" }}>{t.subtitle}</p>
            </div>

            {/* کارت اصلی: تب‌ها + محتوا */}
            <div style={{ padding: "0 28px", marginTop: -56, animation: "fadeUp .4s ease both", position: "relative", zIndex: 2 }}>
                <div style={{
                    background: "#fff", borderRadius: 18,
                    border: "1px solid #EEF2FF", boxShadow: "0 8px 28px rgba(108,61,255,0.10)",
                    overflow: "hidden",
                }}>
                    <MainTabs active={activeTab} onChange={setActiveTab} t={t} />

                    <div style={{ padding: 18 }}>
                        {activeTab === "roles" ? (
                            <div className="rm-split" style={{ display: "flex", gap: 16 }}>
                                {/* ستون نقش‌ها */}
                                <div className="rm-side" style={{ width: 260, flexShrink: 0 }}>
                                    <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                                        <TextField
                                            value={newRoleName}
                                            onChange={(e) => setNewRoleName(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && handleAddRole()}
                                            placeholder={t.newRolePlaceholder}
                                        />
                                        <button
                                            className="primary-btn"
                                            onClick={handleAddRole}
                                            disabled={addingRole}
                                            style={{
                                                height: 40, padding: "0 14px", borderRadius: 10, border: "none",
                                                background: "#6C3DFF", color: "#fff", fontSize: 11.5, fontWeight: 600,
                                                fontFamily: FONT, cursor: addingRole ? "not-allowed" : "pointer",
                                                display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                                            }}
                                        >
                                            {addingRole
                                                ? <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />
                                                : <Icon name="plus" size={13} color="#fff" />}
                                            {t.addRole}
                                        </button>
                                    </div>

                                    {loadingRoles ? (
                                        <div style={{ padding: "24px 0", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>{t.loading}</div>
                                    ) : (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            {roles.map((role) => {
                                                const isActive = selectedRoleId === role.id;
                                                const isSA = role.name === SUPER_ADMIN_NAME;
                                                return (
                                                    <div
                                                        key={role.id}
                                                        className="role-row"
                                                        onClick={() => selectRole(role)}
                                                        style={{
                                                            display: "flex", alignItems: "center", justifyContent: "space-between",
                                                            padding: "10px 12px", borderRadius: 10,
                                                            border: `1.5px solid ${isActive ? "#6C3DFF" : "#EEF2FF"}`,
                                                            background: isActive ? "#F5F2FF" : "#fff",
                                                        }}
                                                    >
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                                            <span style={{
                                                                width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                background: isSA ? "#FEF2F2" : "#EDE9FF",
                                                            }}>
                                                                <Icon name={isSA ? "lock" : "shield"} size={12} color={isSA ? "#EF4444" : "#6C3DFF"} />
                                                            </span>
                                                            <span style={{
                                                                fontSize: 12, fontWeight: 600, color: "#1E293B",
                                                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                                            }}>
                                                                {role.name}
                                                            </span>
                                                        </div>

                                                        {isSA ? (
                                                            <span style={{
                                                                fontSize: 9, fontWeight: 700, color: "#EF4444",
                                                                background: "#FEF2F2", padding: "2px 8px", borderRadius: 99, flexShrink: 0,
                                                            }}>
                                                                {t.superAdminBadge}
                                                            </span>
                                                        ) : (
                                                            <button
                                                                className="icon-btn"
                                                                title={t.deleteRoleHint}
                                                                onClick={(e) => { e.stopPropagation(); setDeleteTarget(role); }}
                                                                style={{
                                                                    width: 26, height: 26, borderRadius: 8, border: "none",
                                                                    background: "transparent", cursor: "pointer", flexShrink: 0,
                                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                                }}
                                                            >
                                                                <Icon name="trash" size={13} color="#EF4444" />
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* ستون دسترسی‌ها (درخت) */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    {!selectedRoleId ? (
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 0", gap: 12 }}>
                                            <div style={{ width: 48, height: 48, borderRadius: 14, background: "#EDE9FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <Icon name="shield" size={20} color="#6C3DFF" />
                                            </div>
                                            <p style={{ color: "#94A3B8", fontSize: 12, margin: 0 }}>{t.selectRoleHint}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div style={{
                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                marginBottom: 14, flexWrap: "wrap", gap: 8,
                                            }}>
                                                <label style={{
                                                    display: "flex", alignItems: "center", gap: 8, cursor: isSuperAdminSelected ? "not-allowed" : "pointer",
                                                }}>
                                                    <TriCheckbox
                                                        checked={totalSelected === allPermissions.length && allPermissions.length > 0}
                                                        indeterminate={totalSelected > 0 && totalSelected < allPermissions.length}
                                                        onChange={toggleSelectAll}
                                                        disabled={isSuperAdminSelected}
                                                    />
                                                    <span style={{ fontSize: 12, fontWeight: 700, color: "#1E293B" }}>{t.selectAll}</span>
                                                </label>
                                                <span style={{ fontSize: 10.5, color: "#94A3B8" }}>
                                                    {t.selectedCount(totalSelected, allPermissions.length)}
                                                </span>
                                            </div>

                                            {isSuperAdminSelected && (
                                                <div style={{
                                                    display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
                                                    background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10,
                                                    marginBottom: 14, fontSize: 11.5, color: "#9A3412",
                                                }}>
                                                    <Icon name="alert" size={14} color="#F97316" />
                                                    {t.superAdminNote}
                                                </div>
                                            )}

                                            {loadingRolePerms ? (
                                                <div style={{ padding: "40px 0", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>{t.loading}</div>
                                            ) : (
                                                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 480, overflowY: "auto", paddingLeft: 4 }}>
                                                    {grouped.map(({ module, permissions }) => {
                                                        const meta = MODULE_META[module] || { icon: "folder", color: "#94A3B8" };
                                                        const moduleChecked = permissions.every((p) => checkedPermissions.includes(p));
                                                        const moduleSomeChecked = permissions.some((p) => checkedPermissions.includes(p));
                                                        const isExpanded = expandedModule === module;

                                                        return (
                                                            <div
                                                                key={module}
                                                                ref={isExpanded ? expandedModuleRef : null}
                                                                style={{
                                                                    border: "1px solid #EEF2FF", borderRadius: 12,
                                                                    background: isExpanded ? "#FBFCFF" : "transparent",
                                                                    marginBottom: isExpanded ? 4 : 0,
                                                                }}
                                                            >
                                                                <div
                                                                    className="module-row"
                                                                    style={{
                                                                        display: "flex", alignItems: "center", gap: 10,
                                                                        padding: "10px 12px", cursor: "pointer",
                                                                    }}
                                                                    onClick={() => toggleExpandModule(module)}
                                                                >
                                                                    <span onClick={(e) => e.stopPropagation()}>
                                                                        <TriCheckbox
                                                                            checked={moduleChecked}
                                                                            indeterminate={moduleSomeChecked && !moduleChecked}
                                                                            onChange={() => toggleModule(permissions)}
                                                                            disabled={isSuperAdminSelected}
                                                                        />
                                                                    </span>
                                                                    <span style={{
                                                                        width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                                        background: `${meta.color}14`,
                                                                    }}>
                                                                        <Icon name={meta.icon} size={12} color={meta.color} />
                                                                    </span>
                                                                    <span style={{ fontSize: 12, fontWeight: 700, color: "#1E293B", flex: 1 }}>
                                                                        {t.permissionModules[module] ?? module}
                                                                    </span>

                                                                    {SCOPABLE_MODULES.includes(module) && !isSuperAdminSelected && (
                                                                        <span onClick={(e) => e.stopPropagation()} title={t.scopeLabel}>
                                                                            <select
                                                                                value={roleScopes[module] ?? "Self"}
                                                                                onChange={(e) => handleScopeChange(module, e.target.value)}
                                                                                disabled={loadingScopes || savingScopeModule === module}
                                                                                style={{
                                                                                    height: 26, fontSize: 10, fontFamily: FONT,
                                                                                    border: "1px solid #E8ECF4", borderRadius: 7,
                                                                                    padding: "0 6px", background: "#fff", color: "#475569",
                                                                                    cursor: savingScopeModule === module ? "not-allowed" : "pointer",
                                                                                }}
                                                                            >
                                                                                {SCOPE_VALUES.map((sv) => (
                                                                                    <option key={sv} value={sv}>{t.scopeLabels[sv]}</option>
                                                                                ))}
                                                                            </select>
                                                                        </span>
                                                                    )}

                                                                    <span style={{
                                                                        fontSize: 9.5, fontWeight: 700, color: meta.color,
                                                                        background: `${meta.color}14`, padding: "2px 8px", borderRadius: 99,
                                                                    }}>
                                                                        {permissions.filter((p) => checkedPermissions.includes(p)).length}/{permissions.length}
                                                                    </span>
                                                                    <span style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .15s" }}>
                                                                        <Icon name="chevron-down" size={13} color="#94A3B8" />
                                                                    </span>
                                                                </div>

                                                                {isExpanded && (
                                                                    <div style={{
                                                                        display: "grid",
                                                                        gridTemplateColumns: "repeat(auto-fit, minmax(110px, max-content))",
                                                                        gap: 8,
                                                                        padding: "10px 12px 14px 44px", borderTop: "1px solid #F5F6FA",
                                                                    }}>
                                                                        {permissions.map((key) => {
                                                                            const [, action] = key.split(".");
                                                                            const isChecked = checkedPermissions.includes(key);
                                                                            return (
                                                                                <label
                                                                                    key={key}
                                                                                    style={{
                                                                                        display: "flex", alignItems: "center", gap: 6,
                                                                                        padding: "6px 10px", borderRadius: 8,
                                                                                        cursor: isSuperAdminSelected ? "not-allowed" : "pointer",
                                                                                        background: isChecked ? "#F5F2FF" : "#F8FAFF",
                                                                                        border: `1px solid ${isChecked ? "#DDD6FE" : "#EEF2FF"}`,
                                                                                        fontSize: 11.5, color: "#334155", fontWeight: 500,
                                                                                        whiteSpace: "nowrap",
                                                                                    }}
                                                                                >
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={isChecked}
                                                                                        onChange={() => togglePermission(key)}
                                                                                        disabled={isSuperAdminSelected}
                                                                                        style={{ accentColor: "#6C3DFF", cursor: "inherit" }}
                                                                                    />
                                                                                    {t.permissionActions[action] ?? action}
                                                                                </label>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {!isSuperAdminSelected && (
                                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                                                    <button
                                                        className="primary-btn"
                                                        onClick={handleSavePermissions}
                                                        disabled={saving}
                                                        style={{
                                                            height: 40, padding: "0 22px", borderRadius: 10, border: "none",
                                                            background: saving ? "#A78BFA" : "#6C3DFF", color: "#fff",
                                                            fontSize: 12, fontWeight: 700, fontFamily: FONT,
                                                            cursor: saving ? "not-allowed" : "pointer",
                                                            display: "flex", alignItems: "center", gap: 8,
                                                            boxShadow: "0 4px 12px rgba(108,61,255,.3)",
                                                        }}
                                                    >
                                                        {saving && <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />}
                                                        {saving ? t.saving : t.save}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="rm-split" style={{ display: "flex", gap: 16 }}>
                                {/* ستون کاربران */}
                                <div className="rm-side" style={{ width: 280, flexShrink: 0 }}>
                                    <div style={{ position: "relative", marginBottom: 12 }}>
                                        <input
                                            value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                            placeholder={t.searchUserPlaceholder}
                                            style={{ ...fieldBaseStyle, padding: "0 36px 0 12px" }}
                                        />
                                        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
                                            <Icon name="search" size={13} color="#94A3B8" />
                                        </span>
                                    </div>

                                    {loadingUsers ? (
                                        <div style={{ padding: "24px 0", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>{t.loading}</div>
                                    ) : visibleUsers.length === 0 ? (
                                        <div style={{ padding: "24px 0", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>{t.noSearchResult}</div>
                                    ) : (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 480, overflowY: "auto" }}>
                                            {visibleUsers.map((user) => {
                                                const isActive = selectedUserId === user.id;
                                                return (
                                                    <div
                                                        key={user.id}
                                                        className="user-row"
                                                        onClick={() => selectUser(user)}
                                                        style={{
                                                            display: "flex", alignItems: "center", gap: 8,
                                                            padding: "10px 12px", borderRadius: 10,
                                                            border: `1.5px solid ${isActive ? "#6C3DFF" : "#EEF2FF"}`,
                                                            background: isActive ? "#F5F2FF" : "#fff",
                                                        }}
                                                    >
                                                        <span style={{
                                                            width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            background: "linear-gradient(135deg, #6C3DFF, #9B59FF)",
                                                            color: "#fff", fontSize: 11, fontWeight: 700,
                                                        }}>
                                                            {user.userName?.[0]?.toUpperCase() ?? "?"}
                                                        </span>
                                                        <span style={{
                                                            fontSize: 12, fontWeight: 600, color: "#1E293B",
                                                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                                        }}>
                                                            {user.userName}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* ستون نقش‌های کاربر انتخاب‌شده */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    {!selectedUserId ? (
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 0", gap: 12 }}>
                                            <div style={{ width: 48, height: 48, borderRadius: 14, background: "#EDE9FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <Icon name="user-plus" size={20} color="#6C3DFF" />
                                            </div>
                                            <p style={{ color: "#94A3B8", fontSize: 12, margin: 0 }}>{t.selectUserHint}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                                                <span style={{
                                                    width: 36, height: 36, borderRadius: "50%",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    background: "linear-gradient(135deg, #6C3DFF, #9B59FF)",
                                                    color: "#fff", fontSize: 13, fontWeight: 700,
                                                }}>
                                                    {selectedUser?.userName?.[0]?.toUpperCase() ?? "?"}
                                                </span>
                                                <span style={{ fontSize: 14, fontWeight: 700, color: "#1E293B" }}>{selectedUser?.userName}</span>
                                            </div>

                                            <p style={{ fontSize: 11.5, fontWeight: 700, color: "#64748B", margin: "0 0 10px" }}>{t.currentRolesTitle}</p>

                                            {loadingUserRoles ? (
                                                <div style={{ padding: "16px 0", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>{t.loading}</div>
                                            ) : userRoles.length === 0 ? (
                                                <p style={{ fontSize: 12, color: "#94A3B8", margin: "0 0 20px" }}>{t.noRolesAssigned}</p>
                                            ) : (
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                                                    {userRoles.map((roleName) => {
                                                        const isSA = roleName === SUPER_ADMIN_NAME;
                                                        return (
                                                            <span
                                                                key={roleName}
                                                                style={{
                                                                    display: "flex", alignItems: "center", gap: 6,
                                                                    padding: "6px 8px 6px 12px", borderRadius: 99,
                                                                    background: isSA ? "#FEF2F2" : "#EDE9FF",
                                                                    color: isSA ? "#EF4444" : "#6C3DFF",
                                                                    fontSize: 11.5, fontWeight: 600,
                                                                }}
                                                            >
                                                                {roleName}
                                                                {(!isSA || isPrimaryAdmin) && (
                                                                    <button
                                                                        className="chip-x"
                                                                        title={t.removeRoleFromUser}
                                                                        onClick={() => handleRemoveRoleFromUser(roleName)}
                                                                        disabled={removingRoleName === roleName}
                                                                        style={{
                                                                            width: 18, height: 18, borderRadius: "50%", border: "none",
                                                                            background: "transparent", cursor: "pointer",
                                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                                        }}
                                                                    >
                                                                        <Icon name="close" size={10} color={isSA ? "#EF4444" : "#6C3DFF"} />
                                                                    </button>
                                                                )}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {assignableRoles.length === 0 ? (
                                                <p style={{ fontSize: 11.5, color: "#94A3B8" }}>{t.noMoreRolesToAssign}</p>
                                            ) : (
                                                <div style={{ display: "flex", gap: 8, alignItems: "center", maxWidth: 380 }}>
                                                    <select
                                                        value={roleToAssign}
                                                        onChange={(e) => setRoleToAssign(e.target.value)}
                                                        style={{ ...fieldBaseStyle, cursor: "pointer" }}
                                                    >
                                                        <option value="">{t.selectRoleToAssign}</option>
                                                        {assignableRoles.map((r) => (
                                                            <option key={r.id} value={r.name}>{r.name}</option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        className="primary-btn"
                                                        onClick={handleAssignRole}
                                                        disabled={!roleToAssign || assigning}
                                                        style={{
                                                            height: 40, padding: "0 16px", borderRadius: 10, border: "none",
                                                            background: !roleToAssign ? "#C4B5FD" : assigning ? "#A78BFA" : "#6C3DFF",
                                                            color: "#fff", fontSize: 11.5, fontWeight: 600, fontFamily: FONT,
                                                            cursor: !roleToAssign || assigning ? "not-allowed" : "pointer",
                                                            whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6,
                                                        }}
                                                    >
                                                        {assigning && <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />}
                                                        {assigning ? t.assigning : t.assignRole}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* مودال تایید حذف نقش */}
            {deleteTarget && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1600, padding: 16 }}>
                    <div style={{ background: "#fff", borderRadius: 18, width: "min(360px, 100%)", padding: 20, boxShadow: "0 20px 60px rgba(15,23,42,.25)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Icon name="trash" size={15} color="#EF4444" />
                            </div>
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1E293B" }}>{t.confirmDeleteTitle}</p>
                        </div>
                        <p style={{ fontSize: 12, color: "#64748B", margin: "0 0 18px", lineHeight: 1.7 }}>
                            {t.confirmDeleteMsg(deleteTarget.name)}
                        </p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <button
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                                style={{
                                    height: 38, padding: "0 16px", borderRadius: 10, border: "1.5px solid #E8ECF4",
                                    background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: "pointer",
                                }}
                            >
                                {t.cancelDelete}
                            </button>
                            <button
                                onClick={handleDeleteRole}
                                disabled={deleting}
                                style={{
                                    height: 38, padding: "0 18px", borderRadius: 10, border: "none",
                                    background: "#EF4444", color: "#fff", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: deleting ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", gap: 8,
                                }}
                            >
                                {deleting && <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />}
                                {deleting ? t.deleting : t.yesDelete}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Toast message={toast.message} visible={toast.visible} type={toast.type} />
        </div>
    );
}